import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Icon } from '@livable/saas-react-components';
import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material';
import React, { useMemo, useState } from 'react';
import useModalFooterStyles from 'src/styles/cutomModalFooter';
import useStyles from './styles';
import { DATE_FORMAT, SCOPE, duplicateHeadersOf, headerFor, headersOf } from './templateModel';

/**
 * Builds one template: which Livable fields go into the file, under which headers, in which order.
 *
 * Columns are held as a flat ordered array rather than a map keyed by field, because order is part
 * of what is being configured and a PMS may well want the same field twice (a unit id in column 1
 * that the import matches on, and again at the end for a human reading the file).
 */
export const TemplateEditor = _ref => {
  var _template$name, _template$dateFormat, _template$isDefault;
  let fields = _ref.fields,
    template = _ref.template,
    propertyId = _ref.propertyId,
    canScopeToProperty = _ref.canScopeToProperty,
    onCancel = _ref.onCancel,
    onSave = _ref.onSave,
    isSaving = _ref.isSaving;
  const classes = useStyles();
  const footerClasses = useModalFooterStyles({
    modalHasNoMargin: true,
    modalHasNoPadding: true
  });
  const _useState = useState((_template$name = template === null || template === void 0 ? void 0 : template.name) !== null && _template$name !== void 0 ? _template$name : ''),
    _useState2 = _slicedToArray(_useState, 2),
    name = _useState2[0],
    setName = _useState2[1];
  const _useState3 = useState((_template$dateFormat = template === null || template === void 0 ? void 0 : template.dateFormat) !== null && _template$dateFormat !== void 0 ? _template$dateFormat : DATE_FORMAT.iso),
    _useState4 = _slicedToArray(_useState3, 2),
    dateFormat = _useState4[0],
    setDateFormat = _useState4[1];
  const _useState5 = useState((_template$isDefault = template === null || template === void 0 ? void 0 : template.isDefault) !== null && _template$isDefault !== void 0 ? _template$isDefault : false),
    _useState6 = _slicedToArray(_useState5, 2),
    isDefault = _useState6[0],
    setIsDefault = _useState6[1];
  const _useState7 = useState(() => {
      if (template) return template.propertyId ? SCOPE.property : SCOPE.account;
      return canScopeToProperty ? SCOPE.property : SCOPE.account;
    }),
    _useState8 = _slicedToArray(_useState7, 2),
    scope = _useState8[0],
    setScope = _useState8[1];
  const _useState9 = useState(() => {
      var _template$columns;
      return ((_template$columns = template === null || template === void 0 ? void 0 : template.columns) !== null && _template$columns !== void 0 ? _template$columns : []).map(column => {
        var _column$customHeader, _column$staticValueOv;
        return {
          livableField: column.livableField,
          customHeader: (_column$customHeader = column.customHeader) !== null && _column$customHeader !== void 0 ? _column$customHeader : '',
          staticValueOverride: (_column$staticValueOv = column.staticValueOverride) !== null && _column$staticValueOv !== void 0 ? _column$staticValueOv : ''
        };
      });
    }),
    _useState0 = _slicedToArray(_useState9, 2),
    columns = _useState0[0],
    setColumns = _useState0[1];
  const _useState1 = useState({}),
    _useState10 = _slicedToArray(_useState1, 2),
    expandedAdvanced = _useState10[0],
    setExpandedAdvanced = _useState10[1];
  const isEdit = Boolean(template);
  const fieldsById = useMemo(() => new Map(fields.map(field => [field.id, field])), [fields]);
  const groups = useMemo(() => {
    const byGroup = new Map();
    fields.forEach(field => {
      if (!byGroup.has(field.group)) byGroup.set(field.group, []);
      byGroup.get(field.group).push(field);
    });
    return [...byGroup.entries()];
  }, [fields]);
  const headers = headersOf(columns, fieldsById);
  const duplicateHeaders = duplicateHeadersOf(headers);
  const toggleField = fieldId => {
    setColumns(current => {
      const index = current.findIndex(column => column.livableField === fieldId);
      if (index === -1) {
        return [...current, {
          livableField: fieldId,
          customHeader: '',
          staticValueOverride: ''
        }];
      }
      return current.filter((_, i) => i !== index);
    });
  };
  const updateColumn = (index, patch) => setColumns(current => current.map((column, i) => i === index ? _objectSpread(_objectSpread({}, column), patch) : column));
  const moveColumn = (index, delta) => setColumns(current => {
    const target = index + delta;
    if (target < 0 || target >= current.length) return current;
    const next = [...current];
    var _ref2 = [next[target], next[index]];
    next[index] = _ref2[0];
    next[target] = _ref2[1];
    return next;
  });
  const handleSave = () => onSave({
    templateId: template === null || template === void 0 ? void 0 : template.id,
    name: name.trim(),
    // Scope is fixed once a template exists — the backend rejects a move — so an edit always
    // sends back the scope it already had.
    propertyId: scope === SCOPE.property ? propertyId : null,
    dateFormat,
    isDefault,
    columns: columns.map((column, index) => ({
      livableField: column.livableField,
      customHeader: column.customHeader.trim() || null,
      staticValueOverride: column.staticValueOverride.trim() || null,
      position: index
    }))
  });
  const canSave = Boolean(name.trim()) && columns.length > 0 && duplicateHeaders.length === 0 && !isSaving;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.editorTopRow
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Template name",
    value: name,
    onChange: event => setName(event.target.value),
    inputProps: {
      maxLength: 128
    },
    size: "small",
    sx: {
      minWidth: 260
    }
  }), /*#__PURE__*/React.createElement(FormControl, {
    size: "small",
    sx: {
      minWidth: 220
    }
  }, /*#__PURE__*/React.createElement(InputLabel, {
    id: "charge-export-date-format"
  }, "Date format"), /*#__PURE__*/React.createElement(Select, {
    labelId: "charge-export-date-format",
    label: "Date format",
    value: dateFormat,
    onChange: event => setDateFormat(event.target.value)
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: DATE_FORMAT.iso
  }, "ISO 8601 (2026-08-26)"), /*#__PURE__*/React.createElement(MenuItem, {
    value: DATE_FORMAT.us
  }, "US (08/26/2026)")))), /*#__PURE__*/React.createElement("div", {
    className: classes.scopeRow
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    row: true,
    value: scope,
    onChange: event => setScope(event.target.value)
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    value: SCOPE.property,
    control: /*#__PURE__*/React.createElement(Radio, {
      size: "small"
    }),
    label: "This property",
    disabled: isEdit || !canScopeToProperty
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    value: SCOPE.account,
    control: /*#__PURE__*/React.createElement(Radio, {
      size: "small"
    }),
    label: "All properties",
    disabled: isEdit
  })), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      size: "small",
      checked: isDefault,
      onChange: event => setIsDefault(event.target.checked)
    }),
    label: "Use as the default"
  }), isEdit ? /*#__PURE__*/React.createElement("span", {
    className: classes.scopeHint
  }, "Scope cannot be changed after a template is created.") : null), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.columnsLayout, " ").concat(classes.mdSection)
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Fields"), /*#__PURE__*/React.createElement("div", {
    className: classes.pane
  }, groups.map(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      group = _ref4[0],
      groupFields = _ref4[1];
    return /*#__PURE__*/React.createElement("div", {
      key: group
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.fieldGroupTitle
    }, group), groupFields.map(field => /*#__PURE__*/React.createElement("div", {
      className: classes.fieldOption,
      key: field.id
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        size: "small",
        checked: columns.some(column => column.livableField === field.id),
        onChange: () => toggleField(field.id)
      }),
      label: field.label
    }), field.isAlwaysEmpty ? /*#__PURE__*/React.createElement("span", {
      className: classes.fieldOptionNote
    }, "exports empty") : null)));
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Columns, in file order", columns.length > 0 ? /*#__PURE__*/React.createElement("span", {
    className: classes.paneLabelCount
  }, " \xB7 ".concat(columns.length)) : null), /*#__PURE__*/React.createElement("div", {
    className: classes.pane
  }, columns.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: classes.paneEmpty
  }, "Pick the fields your PMS import expects.") : null, columns.map((column, index) => {
    var _field$label;
    const field = fieldsById.get(column.livableField);
    const advancedKey = "".concat(column.livableField, "_").concat(index);
    const isAdvancedOpen = expandedAdvanced[advancedKey] || Boolean(column.staticValueOverride);
    return /*#__PURE__*/React.createElement("div", {
      className: classes.columnCard,
      key: advancedKey
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.columnCardTop
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.columnPosition
    }, index + 1), /*#__PURE__*/React.createElement(TextField, {
      placeholder: headerFor(column, fieldsById),
      label: "Column header",
      value: column.customHeader,
      onChange: event => updateColumn(index, {
        customHeader: event.target.value
      }),
      inputProps: {
        maxLength: 128
      },
      size: "small",
      fullWidth: true
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.columnCardActions
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      onClick: () => moveColumn(index, -1),
      disabled: index === 0,
      "aria-label": "move column up"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "arrow-up-circle"
    })), /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      onClick: () => moveColumn(index, 1),
      disabled: index === columns.length - 1,
      "aria-label": "move column down"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "arrow-down-circle"
    })), /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      onClick: () => toggleField(column.livableField),
      "aria-label": "remove column"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "trash"
    })))), /*#__PURE__*/React.createElement("div", {
      className: classes.columnCardMeta
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.columnCardField
    }, (_field$label = field === null || field === void 0 ? void 0 : field.label) !== null && _field$label !== void 0 ? _field$label : column.livableField), column.staticValueOverride ? /*#__PURE__*/React.createElement("span", {
      className: classes.staticChip
    }, "= ".concat(column.staticValueOverride)) : null, field !== null && field !== void 0 && field.isAlwaysEmpty && !column.staticValueOverride ? /*#__PURE__*/React.createElement("span", {
      className: classes.columnWarning
    }, "exports empty") : null, /*#__PURE__*/React.createElement(Button, {
      className: classes.advancedToggle,
      onClick: () => setExpandedAdvanced(current => _objectSpread(_objectSpread({}, current), {}, {
        [advancedKey]: !current[advancedKey]
      }))
    }, isAdvancedOpen ? 'Hide static value' : 'Static value')), isAdvancedOpen ? /*#__PURE__*/React.createElement("div", {
      className: classes.staticValueField
    }, /*#__PURE__*/React.createElement(TextField, {
      label: "Static value (optional)",
      placeholder: "e.g. WATS",
      helperText: "Written into every row of this column instead of the field's value.",
      value: column.staticValueOverride,
      onChange: event => updateColumn(index, {
        staticValueOverride: event.target.value
      }),
      inputProps: {
        maxLength: 256
      },
      size: "small",
      fullWidth: true
    })) : null);
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Header preview"), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.preview, " ").concat(headers.length === 0 ? classes.previewEmpty : '')
  }, headers.length > 0 ? headers.join(',') : 'No columns selected yet.'), duplicateHeaders.length > 0 ? /*#__PURE__*/React.createElement("span", {
    className: classes.warning
  }, "Two columns share a header (".concat(duplicateHeaders.join(', '), "). A PMS import matches on the header, so each must be unique.")) : null), /*#__PURE__*/React.createElement("div", {
    className: "".concat(footerClasses.modalFooter, " ").concat(footerClasses.footerEven)
  }, /*#__PURE__*/React.createElement("div", {
    className: footerClasses.footerWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    onClick: onCancel,
    disabled: isSaving
  }, "Cancel")), /*#__PURE__*/React.createElement("div", {
    className: footerClasses.footerWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: footerClasses.footerButton
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleSave,
    disabled: !canSave
  }, isEdit ? 'Save changes' : 'Create template')))));
};